// http.ts
import axios, { AxiosError, type AxiosInstance } from 'axios'
import { getAuthToken, clearAuthToken } from './authToken'
import { showToast } from './toast'
import { i18n } from '../plugins/i18n'
import { getDParam } from '../store/domainStore'
import { getApiUrl } from '../store/apiUrlStore'

export type FieldErrors = Record<string, string>

export type ApiError = AxiosError & {
  fieldErrors?: FieldErrors | null
}

export type RequestMeta = {
  suppressToast?: boolean
}

export type RequestConfigWithMeta = AxiosError['config'] & {
  meta?: RequestMeta
}

export function isNetworkFailure(error: unknown): boolean {
  const e = error as ApiError | undefined
  return !!e && !e.response
}

export function extractErrorMessage(error: AxiosError): string {
  const data: any = error.response?.data
  return (
    data?.message ||
    data?.error?.message ||
    data?.errors?.[0] ||
    error.message ||
    i18n.global.t('network-error')
  )
}

function extractFieldErrors(error: AxiosError): FieldErrors | null {
  const data: any = error.response?.data
  const raw = data?.errors ?? data?.fieldErrors
  if (!raw || typeof raw !== 'object') return null

  const out: FieldErrors = {}
  for (const [key, val] of Object.entries(raw)) {
    if (Array.isArray(val)) out[key] = String(val[0] ?? '')
    else if (val != null) out[key] = String(val)
  }
  return Object.keys(out).length ? out : null
}

export function createHttpClient(): AxiosInstance {
  const http = axios.create({
    headers: { Accept: 'application/json' },
    timeout: 30_000,
  })

  http.interceptors.request.use(async (config) => {
    const token = await getAuthToken()
    if (token) {
      config.headers = config.headers ?? {}
      config.headers.Authorization = `Bearer ${token}`
    }
    const apiOverride: string | null = getDParam()
    const defaultApi = await getApiUrl()

    config.baseURL = (apiOverride || defaultApi || '').replace(/\/+$/, '')
    return config
  })

  http.interceptors.response.use(
    (r) => r,
    (error: AxiosError) => {
      const cfg = (error.config ?? {}) as RequestConfigWithMeta
      const status = error.response?.status
      const message = extractErrorMessage(error)

      if (!cfg.meta?.suppressToast) {
        showToast({ message, variant: 'danger' })
      }

      if (status === 401) {
        clearAuthToken().then(() => {
          ;(error as ApiError).fieldErrors = extractFieldErrors(error)
        })
      }

      return Promise.reject(error)
    }
  )

  return http
}

export const http = createHttpClient()
